import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config/api';

const PAGE_W         = 794;
const PAGE_H         = 1123;
const PAD            = 20;
const COLS           = 2;
const ROWS           = 4;
const GAP            = 8;
const CARD_W         = Math.floor((PAGE_W - PAD * 2 - GAP * (COLS - 1)) / COLS);
const CARD_H         = Math.round(CARD_W * (54 / 85.6));
const CARDS_PER_PAGE = COLS * ROWS;

const PRINT_STYLE = `
  @media print {
    @page { size: 210mm 297mm portrait; margin: 0; }
    body > * { display: none !important; }
    #id-card-print-root { display: block !important; position: static !important; background: transparent !important; overflow: visible !important; padding: 0 !important; }
    .print-page { page-break-after: always; box-shadow: none !important; transform: none !important; width: 210mm !important; height: 297mm !important; }
    .print-page:last-child { page-break-after: avoid; }
  }
`;

const CardGrid = ({ students, idCards, CardComponent }) => (
  <div style={{
    display: 'grid',
    gridTemplateColumns: `repeat(${COLS}, ${CARD_W}px)`,
    gridTemplateRows: `repeat(${ROWS}, ${CARD_H}px)`,
    gap: GAP,
    flexShrink: 0,
  }}>
    {Array.from({ length: CARDS_PER_PAGE }).map((_, i) => {
      const s = students[i];
      return s
        ? <CardComponent key={s._id} student={s} idCard={idCards[s._id]} />
        : <div key={`empty-${i}`} style={{ width: CARD_W, height: CARD_H, background: '#f3f4f6', borderRadius: 6, border: '1px dashed #d1d5db' }} />;
    })}
  </div>
);

const IdCardFront = ({ student, idCard, academicYear = '2026-2027' }) => {
  const shortYear = academicYear.replace(/^(\d{4})-(\d{2})(\d{2})$/, '$1-$3');
  const validYear = academicYear.split('-')[1] || '2027';
  const headerH = Math.round(CARD_H * 0.30);
  const bodyH   = CARD_H - headerH;

  return (
    <div style={{
      width: CARD_W, height: CARD_H,
      background: 'linear-gradient(135deg,#1e3a8a 0%,#1e40af 100%)',
      borderRadius: 6, border: '1.5px solid #93c5fd',
      position: 'relative', overflow: 'hidden',
      boxSizing: 'border-box',
      fontFamily: 'Arial,sans-serif', display: 'flex', flexDirection: 'column',
    }}>
      <div style={{
        background: '#fff', height: headerH,
        padding: '5px 8px', display: 'flex',
        alignItems: 'center', justifyContent: 'space-between',
        boxSizing: 'border-box', flexShrink: 0,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <div style={{
            width: headerH - 12, height: headerH - 12,
            background: '#1e40af', borderRadius: 4,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            overflow: 'hidden', flexShrink: 0,
          }}>
            <img src="/logo.png" alt=""
              style={{ width: '90%', height: '90%', objectFit: 'contain' }}
              onError={e => { e.target.style.display = 'none'; e.target.parentElement.innerText = '🏫'; }}
            />
          </div>
          <div>
            <div style={{ color: '#1e40af', fontWeight: 'bold', fontSize: 10, lineHeight: 1.3 }}>The Rising School</div>
            <div style={{ color: '#64748b', fontSize: 8.5, lineHeight: 1.3 }}>Savina, Udaipur</div>
            <div style={{ color: '#64748b', fontSize: 8.5, lineHeight: 1.3 }}>8209683549</div>
          </div>
        </div>
        <div style={{
          background: '#dc2626', color: '#fff',
          padding: '3px 6px', borderRadius: 4,
          fontSize: 8.5, fontWeight: 'bold', flexShrink: 0,
        }}>{shortYear}</div>
      </div>

      <div style={{
        height: bodyH, padding: '6px 8px',
        display: 'flex', gap: 8, boxSizing: 'border-box', flex: 1,
      }}>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0 }}>
          <div style={{
            width: Math.round(bodyH * 0.60),
            height: Math.round(bodyH * 0.60),
            background: student?.profilePicture
              ? `url(${API_BASE_URL}${student.profilePicture}) center/cover no-repeat`
              : '#e5e7eb',
            border: '2px solid #fff', borderRadius: 4,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 22, color: '#9ca3af', overflow: 'hidden',
          }}>
            {!student?.profilePicture && '👤'}
          </div>
          <div style={{
            marginTop: 3, background: '#fff', padding: '1px 5px',
            borderRadius: 3, fontSize: 7.5, fontWeight: 'bold',
            color: '#1e40af', whiteSpace: 'nowrap',
          }}>
            {idCard?.scholarNo || student?.studentId || 'N/A'}
          </div>
        </div>

        <div style={{ flex: 1, color: '#fff', overflow: 'hidden', minWidth: 0 }}>
          <div style={{
            fontWeight: 'bold', fontSize: 12, lineHeight: 1.2,
            marginBottom: 3, whiteSpace: 'nowrap',
            overflow: 'hidden', textOverflow: 'ellipsis',
          }}>
            {student?.name || 'N/A'}
          </div>
          <div style={{ fontSize: 9.5, color: 'rgba(255,255,255,0.85)', marginBottom: 4 }}>
            Class: {student?.class || 'N/A'}
          </div>
          <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.9)', lineHeight: 1.7 }}>
            <div><b>DOB:</b> {idCard?.dob ? new Date(idCard.dob).toLocaleDateString('en-GB') : 'N/A'}</div>
            <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              <b>Father:</b> {idCard?.fathersName || 'N/A'}
            </div>
            <div><b>Mob:</b> {idCard?.mobile || 'N/A'}</div>
          </div>
        </div>
      </div>

      <div style={{
        position: 'absolute', bottom: 4, right: 6,
        background: 'rgba(255,255,255,0.18)', padding: '1px 5px',
        borderRadius: 3, fontSize: 7, color: '#fff',
      }}>Valid: Mar {validYear}</div>
    </div>
  );
};

const IdCardBack = ({ student, idCard, academicYear = '2026-2027' }) => {
  const shortYear = academicYear.replace(/^(\d{4})-(\d{2})(\d{2})$/, '$1-$3');
  return (
    <div style={{
      width: CARD_W, height: CARD_H,
      background: 'linear-gradient(135deg,#1e3a8a 0%,#1e40af 100%)',
      borderRadius: 6, border: '1.5px solid #93c5fd',
      overflow: 'hidden', boxSizing: 'border-box',
      display: 'flex', flexDirection: 'column', justifyContent: 'space-between',
      padding: '8px 10px', fontFamily: 'Arial,sans-serif',
    }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8,
        borderBottom: '1px solid rgba(255,255,255,0.3)', paddingBottom: 6,
      }}>
        <div style={{
          width: 36, height: 36, background: '#fff', borderRadius: 4,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          overflow: 'hidden', flexShrink: 0,
        }}>
          <img src="/logo.png" alt=""
            style={{ width: 30, height: 30, objectFit: 'contain' }}
            onError={e => { e.target.style.display = 'none'; e.target.parentElement.innerText = '🏫'; }}
          />
        </div>
        <div style={{ color: '#fff' }}>
          <div style={{ fontWeight: 'bold', fontSize: 11, lineHeight: 1.3 }}>The Rising School</div>
          <div style={{ fontSize: 8.5, opacity: 0.85 }}>Sector 12 Savina, Udaipur</div>
        </div>
      </div>

      <div style={{ color: '#fff', textAlign: 'center' }}>
        <div style={{ fontWeight: 'bold', fontSize: 12, lineHeight: 1.2 }}>{student?.name || 'N/A'}</div>
        <div style={{ fontSize: 9, opacity: 0.85, marginTop: 2 }}>
          Class: {student?.class || 'N/A'} &nbsp;|&nbsp; SR: {idCard?.scholarNo || student?.studentId || 'N/A'}
        </div>
      </div>

      <div style={{ background: 'rgba(255,255,255,0.13)', borderRadius: 4, padding: '5px 8px' }}>
        <div style={{ color: '#fff', fontSize: 8, lineHeight: 1.7 }}>
          <div style={{ fontWeight: 'bold', fontSize: 8.5, marginBottom: 2 }}>Rules &amp; Regulations:</div>
          <div>• Carry this card at all times in school</div>
          <div>• If found, please return to school office</div>
          <div>• Valid for Academic Year {academicYear} only</div>
          <div>• Report loss of card immediately</div>
        </div>
      </div>

      <div style={{ color: 'rgba(255,255,255,0.8)', fontSize: 8, textAlign: 'center' }}>
        8209683549 &nbsp;|&nbsp; www.therisingschool.com
      </div>
    </div>
  );
};

const IdCardPage = React.forwardRef(
  ({ pageStudents, idCards, pageNum, totalPages, pageLabel, side, academicYear }, ref) => (
    <div ref={ref} className="print-page" style={{
      width: PAGE_W, height: PAGE_H,
      background: '#fff', padding: PAD,
      boxSizing: 'border-box', fontFamily: 'Arial,sans-serif',
      display: 'flex', flexDirection: 'column',
    }}>
      <div style={{
        textAlign: 'center', fontSize: 9, color: '#6b7280',
        borderBottom: '0.8px solid #e5e7eb', paddingBottom: 4, marginBottom: 8, flexShrink: 0,
      }}>
        The Rising School — {pageLabel} — Page {pageNum} of {totalPages}
      </div>

      <div style={{
        fontSize: 9, fontWeight: 'bold',
        color: side === 'front' ? '#1e40af' : '#7c3aed',
        marginBottom: 6, flexShrink: 0,
      }}>
        {side === 'front' ? '▶ FRONT SIDE — Print on Side 1' : '▶ BACK SIDE — Print on Side 2'}
      </div>

      <CardGrid
        students={pageStudents}
        idCards={idCards}
        CardComponent={side === 'front'
          ? (props) => <IdCardFront {...props} academicYear={academicYear} />
          : (props) => <IdCardBack  {...props} academicYear={academicYear} />}
      />

      <div style={{
        marginTop: 'auto', paddingTop: 6,
        textAlign: 'center', fontSize: 7, color: '#9ca3af', flexShrink: 0,
      }}>
        Printed by The Rising School Management System — Academic Year {academicYear}
      </div>
    </div>
  )
);

function BulkIdCardPrint({ user, onClose, academicYear = '2026-2027' }) {
  // ── mode: 'all' | 'selected' ─────────────────────────────────────────────
  const [mode, setMode]                       = useState('all');

  // ── all-mode state ────────────────────────────────────────────────────────
  const [classes, setClasses]                 = useState([]);
  const [selectedClass, setSelectedClass]     = useState('');
  const [students, setStudents]               = useState([]);
  const [idCards, setIdCards]                 = useState({});

  // ── selected-mode state ───────────────────────────────────────────────────
  const [selClass, setSelClass]               = useState('');
  const [selClassStudents, setSelClassStudents] = useState([]);
  const [selClassLoading, setSelClassLoading] = useState(false);
  // allFetched: id -> student object, persists across class switches
  const [allFetched, setAllFetched]           = useState({});
  // allFetchedIdCards: id -> idCard, persists across class switches
  const [allFetchedIdCards, setAllFetchedIdCards] = useState({});
  const [selectedIds, setSelectedIds]         = useState(new Set());

  // ── shared state ──────────────────────────────────────────────────────────
  const [loading, setLoading]                 = useState(false);
  const [downloading, setDownloading]         = useState(false);
  const [error, setError]                     = useState('');
  const [progress, setProgress]               = useState('');
  const [donePages, setDonePages]             = useState(0);
  const pageRefs = useRef([]);

  useEffect(() => { fetchClasses(); }, []);

  const fetchClasses = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/api/users?role=student`);
      const unique = [...new Set(res.data.map(s => s.class).filter(Boolean))].sort((a, b) => {
        const ord = c =>
          c === 'Nursery' ? 0 : c === 'LKG' ? 1 : c === 'UKG' ? 2 : c === 'HKG' ? 3
          : (parseInt(c) || 999) + 4;
        return ord(a) - ord(b);
      });
      setClasses(unique);
    } catch { setError('Failed to fetch classes'); }
  };

  // ── All-mode: fetch class data ────────────────────────────────────────────
  const fetchClassData = async (className) => {
    try {
      setLoading(true); setError('');
      const res = await axios.get(`${API_BASE_URL}/api/users?role=student&class=${className}`);
      const classStudents = res.data;
      setStudents(classStudents);
      pageRefs.current = [];
      const idCardMap = {};
      await Promise.all(classStudents.map(async s => {
        try {
          const r = await axios.get(`${API_BASE_URL}/api/idcard/${s._id}`);
          idCardMap[s._id] = r.data;
        } catch { idCardMap[s._id] = null; }
      }));
      setIdCards(idCardMap);
    } catch { setError('Failed to fetch class data'); }
    finally { setLoading(false); }
  };

  const handleClassChange = cls => {
    setSelectedClass(cls);
    setStudents([]); setIdCards({}); setError('');
    if (cls) fetchClassData(cls);
  };

  // ── Selected-mode: fetch class students ──────────────────────────────────
  const fetchSelClassStudents = async (className) => {
    try {
      setSelClassLoading(true); setError('');
      const res = await axios.get(`${API_BASE_URL}/api/users?role=student&class=${className}`);
      const list = res.data;
      setSelClassStudents(list);

      // Fetch id cards for any new students not yet fetched
      const newStudents = list.filter(s => !allFetched[s._id]);
      const newFetched = { ...allFetched };
      const newIdCards = { ...allFetchedIdCards };
      newStudents.forEach(s => { newFetched[s._id] = s; });
      await Promise.all(newStudents.map(async s => {
        try {
          const r = await axios.get(`${API_BASE_URL}/api/idcard/${s._id}`);
          newIdCards[s._id] = r.data;
        } catch { newIdCards[s._id] = null; }
      }));
      setAllFetched(newFetched);
      setAllFetchedIdCards(newIdCards);
    } catch { setError('Failed to fetch class students'); }
    finally { setSelClassLoading(false); }
  };

  const handleSelClassChange = cls => {
    setSelClass(cls);
    setSelClassStudents([]);
    if (cls) fetchSelClassStudents(cls);
  };

  const toggleStudent = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    const allIds = selClassStudents.map(s => s._id);
    const allSelected = allIds.every(id => selectedIds.has(id));
    setSelectedIds(prev => {
      const next = new Set(prev);
      allIds.forEach(id => allSelected ? next.delete(id) : next.add(id));
      return next;
    });
  };

  const clearSelection = () => setSelectedIds(new Set());

  // ── Derive active student list for preview + PDF ──────────────────────────
  const activeStudents = mode === 'all'
    ? students
    : [...selectedIds].map(id => allFetched[id]).filter(Boolean);

  const activeIdCards = mode === 'all' ? idCards : allFetchedIdCards;

  const pageLabel = mode === 'all'
    ? `Class ${selectedClass} ID Cards`
    : `Selected Students (${activeStudents.length})`;

  const pdfFileName = mode === 'all'
    ? `Class_${selectedClass}_ID_Cards.pdf`
    : `Selected_Students_${activeStudents.length}_ID_Cards.pdf`;

  // ── Batching + render pages ───────────────────────────────────────────────
  const batches = [];
  for (let i = 0; i < activeStudents.length; i += CARDS_PER_PAGE)
    batches.push(activeStudents.slice(i, i + CARDS_PER_PAGE));

  const renderPages = batches.flatMap((batch, bi) => [
    { batch, side: 'front', batchIdx: bi },
    { batch, side: 'back',  batchIdx: bi },
  ]);
  const totalPrintPages = renderPages.length;

  const pct = totalPrintPages ? Math.round((donePages / totalPrintPages) * 100) : 0;

  // ── PDF Download ──────────────────────────────────────────────────────────
  const handleDownloadPDF = async () => {
    if (!renderPages.length) return;
    try {
      setDownloading(true); setDonePages(0); setProgress('Preparing…');
      const html2canvas = (await import('html2canvas')).default;
      const { jsPDF }   = await import('jspdf');
      const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });

      for (let i = 0; i < pageRefs.current.length; i++) {
        setProgress(`Rendering page ${i + 1} of ${pageRefs.current.length}…`);
        const el = pageRefs.current[i];
        if (!el) continue;
        const canvas = await html2canvas(el, {
          scale: 2, useCORS: true, allowTaint: true,
          backgroundColor: '#ffffff', logging: false,
          width: PAGE_W, height: PAGE_H,
        });
        const imgData = canvas.toDataURL('image/jpeg', 0.92);
        if (i > 0) pdf.addPage();
        pdf.addImage(imgData, 'JPEG', 0, 0, 210, 297);
        setDonePages(i + 1);
      }

      setProgress('Saving…');
      pdf.save(pdfFileName);
      setProgress(''); setDonePages(0);
    } catch (err) {
      setError('PDF failed: ' + err.message);
      setProgress('');
    } finally { setDownloading(false); }
  };

  // ── Render ────────────────────────────────────────────────────────────────
  const canDownload = mode === 'all' ? students.length > 0 : selectedIds.size > 0;

  return (
    <div id="id-card-print-root" style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.9)',
      zIndex: 9999, display: 'flex', flexDirection: 'column',
      alignItems: 'center', overflowY: 'auto', padding: '20px 16px',
    }}>
      <style>{PRINT_STYLE}</style>

      {/* ── Control Panel ── */}
      <div style={{
        background: '#fff', borderRadius: 16, padding: 26,
        width: '100%', maxWidth: 640, marginBottom: 24, flexShrink: 0,
      }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 }}>
          <h2 style={{ margin: 0, color: '#1e40af', fontSize: 20 }}>🆔 Bulk ID Card Print</h2>
          <button onClick={onClose} style={{
            background: '#dc2626', color: '#fff', border: 'none',
            borderRadius: 8, padding: '7px 14px', cursor: 'pointer', fontWeight: 'bold',
          }}>✕ Close</button>
        </div>

        {/* Mode Toggle */}
        <div style={{
          display: 'flex', gap: 8, marginBottom: 20,
          background: '#f1f5f9', borderRadius: 10, padding: 4,
        }}>
          {[['all', '📋 All Students'], ['selected', '☑️ Selected Students']].map(([val, label]) => (
            <button key={val} onClick={() => { setMode(val); setError(''); pageRefs.current = []; }}
              style={{
                flex: 1, padding: '9px 0', border: 'none', borderRadius: 8,
                fontWeight: 'bold', fontSize: 13, cursor: 'pointer',
                background: mode === val ? 'linear-gradient(135deg,#1e40af,#3b82f6)' : 'transparent',
                color: mode === val ? '#fff' : '#64748b',
                transition: 'all 0.2s',
              }}>{label}</button>
          ))}
        </div>

        {/* ── ALL MODE ── */}
        {mode === 'all' && (
          <>
            <label style={{ display: 'block', fontWeight: 'bold', marginBottom: 6, color: '#374151', fontSize: 14 }}>
              Select Class:
            </label>
            <select value={selectedClass} onChange={e => handleClassChange(e.target.value)} style={{
              width: '100%', padding: '10px 14px', borderRadius: 8,
              border: '2px solid #1e40af', fontSize: 15, cursor: 'pointer', marginBottom: 14,
            }}>
              <option value="">-- Select a Class --</option>
              {classes.map(c => <option key={c} value={c}>Class {c}</option>)}
            </select>
          </>
        )}

        {/* ── SELECTED MODE ── */}
        {mode === 'selected' && (
          <>
            {/* Selected count + clear */}
            <div style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              background: selectedIds.size > 0 ? '#eff6ff' : '#f8fafc',
              border: `2px solid ${selectedIds.size > 0 ? '#3b82f6' : '#e2e8f0'}`,
              borderRadius: 10, padding: '10px 14px', marginBottom: 14,
            }}>
              <div style={{ fontWeight: 'bold', color: '#1e40af', fontSize: 14 }}>
                ☑️ Selected Students: <span style={{ fontSize: 18, color: '#1d4ed8' }}>{selectedIds.size}</span>
              </div>
              {selectedIds.size > 0 && (
                <button onClick={clearSelection} style={{
                  background: '#fee2e2', color: '#dc2626', border: 'none',
                  borderRadius: 6, padding: '5px 12px', cursor: 'pointer',
                  fontWeight: 'bold', fontSize: 12,
                }}>✕ Clear All</button>
              )}
            </div>

            {/* Class picker for selection */}
            <label style={{ display: 'block', fontWeight: 'bold', marginBottom: 6, color: '#374151', fontSize: 14 }}>
              Browse Class to Select Students:
            </label>
            <select value={selClass} onChange={e => handleSelClassChange(e.target.value)} style={{
              width: '100%', padding: '10px 14px', borderRadius: 8,
              border: '2px solid #1e40af', fontSize: 15, cursor: 'pointer', marginBottom: 12,
            }}>
              <option value="">-- Pick a Class --</option>
              {classes.map(c => <option key={c} value={c}>Class {c}</option>)}
            </select>

            {selClassLoading && (
              <div style={{ textAlign: 'center', padding: 14, color: '#1e40af', fontWeight: 'bold' }}>
                ⏳ Loading…
              </div>
            )}

            {selClassStudents.length > 0 && !selClassLoading && (
              <div style={{
                border: '1.5px solid #e2e8f0', borderRadius: 10,
                overflow: 'hidden', marginBottom: 4,
              }}>
                {/* Select-all row */}
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  padding: '8px 14px', background: '#f1f5f9',
                  borderBottom: '1px solid #e2e8f0', cursor: 'pointer',
                }} onClick={toggleSelectAll}>
                  <input type="checkbox" readOnly
                    checked={selClassStudents.every(s => selectedIds.has(s._id))}
                    style={{ width: 16, height: 16, cursor: 'pointer' }}
                  />
                  <span style={{ fontWeight: 'bold', fontSize: 13, color: '#374151' }}>
                    Select All — Class {selClass} ({selClassStudents.length} students)
                  </span>
                </div>

                {/* Student rows */}
                <div style={{ maxHeight: 260, overflowY: 'auto' }}>
                  {selClassStudents.map(s => {
                    const checked = selectedIds.has(s._id);
                    return (
                      <div key={s._id}
                        onClick={() => toggleStudent(s._id)}
                        style={{
                          display: 'flex', alignItems: 'center', gap: 10,
                          padding: '8px 14px', cursor: 'pointer',
                          background: checked ? '#eff6ff' : '#fff',
                          borderBottom: '1px solid #f1f5f9',
                          transition: 'background 0.15s',
                        }}>
                        <input type="checkbox" readOnly checked={checked}
                          style={{ width: 16, height: 16, cursor: 'pointer', flexShrink: 0 }}
                        />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontWeight: checked ? 'bold' : 'normal', fontSize: 13, color: '#1e293b' }}>
                            {s.name}
                          </div>
                          <div style={{ fontSize: 11, color: '#64748b' }}>
                            Class {s.class} &nbsp;·&nbsp; {s.studentId || s._id.slice(-6)}
                          </div>
                        </div>
                        {checked && (
                          <span style={{
                            background: '#1e40af', color: '#fff',
                            fontSize: 10, fontWeight: 'bold',
                            padding: '2px 7px', borderRadius: 20, flexShrink: 0,
                          }}>✓ Selected</span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </>
        )}

        {error && (
          <div style={{ background: '#fee2e2', color: '#dc2626', padding: 10, borderRadius: 8, marginBottom: 12, fontSize: 13 }}>
            ⚠️ {error}
          </div>
        )}
        {loading && (
          <div style={{ textAlign: 'center', padding: 18, color: '#1e40af', fontWeight: 'bold' }}>
            ⏳ Loading student data…
          </div>
        )}

        {/* Summary + Download — shown when there's something to print */}
        {canDownload && !loading && (
          <>
            <div style={{
              background: '#eff6ff', border: '2px solid #1e40af',
              borderRadius: 10, padding: 14, marginBottom: 16, marginTop: 14,
            }}>
              <div style={{ fontWeight: 'bold', color: '#1e40af', marginBottom: 6, fontSize: 14 }}>
                📊 Summary — {mode === 'all' ? `Class ${selectedClass}` : 'Selected Students'}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px 20px', fontSize: 13, color: '#374151' }}>
                <div>👨‍🎓 Students: <b>{activeStudents.length}</b></div>
                <div>📄 Print pages: <b>{totalPrintPages}</b></div>
                <div>🆔 Cards/batch: <b>{CARDS_PER_PAGE}</b></div>
                <div>📐 Layout: <b>2 cols × 4 rows</b></div>
              </div>
              <div style={{ marginTop: 8, fontSize: 11, color: '#6b7280' }}>
                💡 Each batch = 1 Front page + 1 Back page. Print duplex (flip on short edge) for double-sided cards.
              </div>
            </div>

            <button onClick={handleDownloadPDF} disabled={downloading} style={{
              width: '100%', padding: 13,
              background: downloading ? '#9ca3af' : 'linear-gradient(135deg,#1e40af,#3b82f6)',
              color: '#fff', border: 'none', borderRadius: 10,
              fontSize: 15, fontWeight: 'bold',
              cursor: downloading ? 'not-allowed' : 'pointer',
              boxShadow: '0 4px 15px rgba(30,64,175,0.35)',
              marginBottom: 10,
            }}>
              {downloading ? `⏳ ${progress}` : `⬇️ Download PDF — ${totalPrintPages} Pages`}
            </button>

            {downloading && (
              <div>
                <div style={{ background: '#e5e7eb', borderRadius: 6, height: 10, overflow: 'hidden', marginBottom: 4 }}>
                  <div style={{
                    height: '100%',
                    background: 'linear-gradient(90deg,#1e40af,#3b82f6)',
                    width: `${pct}%`,
                    transition: 'width 0.3s ease',
                    borderRadius: 6,
                  }} />
                </div>
                <div style={{ fontSize: 11, color: '#6b7280', textAlign: 'center' }}>
                  {donePages} / {totalPrintPages} pages rendered ({pct}%)
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* ── Full Preview ── */}
      {canDownload && !loading && (
        <div style={{ width: '100%', maxWidth: 860 }}>
          <div style={{
            color: '#fff', fontWeight: 'bold', fontSize: 15,
            marginBottom: 16, textAlign: 'center',
          }}>
            👁️ Preview — {totalPrintPages} Print Pages
            <span style={{ fontSize: 11, fontWeight: 'normal', color: '#9ca3af', marginLeft: 8 }}>
              (scaled to fit screen)
            </span>
          </div>

          {renderPages.map(({ batch, side, batchIdx }, idx) => {
            const previewW = Math.min(820, PAGE_W);
            const scale    = previewW / PAGE_W;
            const scaledH  = Math.round(PAGE_H * scale);
            const batchLabel = `Batch ${batchIdx + 1} — ${side === 'front' ? '▶ FRONT' : '▶ BACK'}`;

            return (
              <div key={idx} style={{ marginBottom: 32 }}>
                <div style={{
                  color: side === 'front' ? '#60a5fa' : '#a78bfa',
                  fontWeight: 'bold', fontSize: 12,
                  marginBottom: 6, textAlign: 'center',
                }}>
                  ── Page {idx + 1} of {totalPrintPages} &nbsp;·&nbsp; {batchLabel} ──
                </div>

                <div style={{
                  width: previewW, height: scaledH,
                  overflow: 'hidden', borderRadius: 10,
                  boxShadow: '0 6px 24px rgba(0,0,0,0.5)',
                  margin: '0 auto',
                }}>
                  <div style={{
                    transform: `scale(${scale})`,
                    transformOrigin: 'top left',
                    width: PAGE_W, height: PAGE_H,
                  }}>
                    <IdCardPage
                      ref={el => { pageRefs.current[idx] = el; }}
                      pageStudents={batch}
                      idCards={activeIdCards}
                      pageNum={idx + 1}
                      totalPages={totalPrintPages}
                      pageLabel={pageLabel}
                      side={side}
                      academicYear={academicYear}
                    />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default BulkIdCardPrint;
